import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { 
  Clock, 
  Calendar,
  ChevronRight,
  ChevronLeft,
  Coffee,
  Sunrise,
  Sun,
  Sunset,
  Moon,
  Star,
  Trash2,
  Plus,
  Minus,
  Target,
  CalendarDays,
  CheckCircle,
  AlertCircle,
  GripVertical,
  Filter,
  Lock,
  Unlock
} from 'lucide-react';

const WeeklyTimeSchedule = ({ 
  selectedTasks = [], 
  weeklySchedule: externalSchedule, 
  setWeeklySchedule: externalSetSchedule,
  onNext = () => {},
  onPrevious = () => {},
  weekDates = [],
  dayNames = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'],
  onTaskDragged = () => {},
  onTaskRemoved = () => {},
  draggedTaskIds = [],
  availableTasks: propAvailableTasks,
  isAddingToExisting = false, // תיקון: הוספת prop חדש
  existingSchedule = {}, // תיקון: הוספת prop חדש
  onTaskClick = () => {} // תיקון: הוספת prop לחיצה על משימה
}) => {
  const { currentUser } = useAuth();
  const { meetings = [] } = useData();
  
  // אם אין weekDates, ניצור אותם
  const defaultWeekDates = React.useMemo(() => {
    if (weekDates.length > 0) return weekDates;
    const dates = [];
    const today = new Date();
    const dayOfWeek = today.getDay();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - dayOfWeek);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(sunday);
      date.setDate(sunday.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [weekDates]);

  // משתמשים ב-state פנימי אם לא קיבלנו פונקציה חיצונית
  const [internalSchedule, setInternalSchedule] = useState({});
  const weeklySchedule = externalSchedule || internalSchedule;
  const setWeeklySchedule = externalSetSchedule || setInternalSchedule;
  
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [taskDurations, setTaskDurations] = useState({});
  const [availableTasks, setAvailableTasks] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState({});
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedFrom, setDraggedFrom] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [alreadyScheduledTasks, setAlreadyScheduledTasks] = useState([]);
  const [dailyPlanBlockedSlots, setDailyPlanBlockedSlots] = useState({}); // תיקון: הוספת state חדש
  const [weeklyPlanBlockedSlots, setWeeklyPlanBlockedSlots] = useState({}); // תיקון: הוספת state חדש
  const [weeklyScheduledMeetings, setWeeklyScheduledMeetings] = useState({}); // ישיבות מתוכננות לכל יום בשבוע
  
  const dragRef = useRef(null);
  const ghostRef = useRef(null);

  const timeSlots = [
    { time: '06:00', label: 'בוקר מוקדם', icon: Sunrise, color: 'from-purple-400 to-pink-400' },
    { time: '06:30', label: '', icon: null, color: 'from-purple-400 to-pink-400' },
    { time: '07:00', label: '', icon: Coffee, color: 'from-orange-400 to-yellow-400' },
    { time: '07:30', label: '', icon: null, color: 'from-orange-400 to-yellow-400' },
    { time: '08:00', label: 'תחילת יום', icon: Sun, color: 'from-yellow-400 to-orange-400' },
    { time: '08:30', label: '', icon: null, color: 'from-yellow-400 to-orange-400' },
    { time: '09:00', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '09:30', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '10:00', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '10:30', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '11:00', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '11:30', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '12:00', label: 'צהריים', icon: Sun, color: 'from-yellow-500 to-orange-500' },
    { time: '12:30', label: '', icon: null, color: 'from-yellow-500 to-orange-500' },
    { time: '13:00', label: '', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '13:30', label: '', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '14:00', label: '', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '14:30', label: '', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '15:00', label: 'אחר הצהריים', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '15:30', label: '', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '16:00', label: '', icon: null, color: 'from-indigo-400 to-purple-400' },
    { time: '16:30', label: '', icon: null, color: 'from-indigo-400 to-purple-400' },
    { time: '17:00', label: '', icon: Sunset, color: 'from-orange-400 to-pink-400' }
  ];

  const currentDate = defaultWeekDates[currentDayIndex];
  const currentDateStr = currentDate?.toISOString().split('T')[0];
  const currentDayName = dayNames[currentDayIndex];

  // תיקון: פונקציה משופרת לבדיקת תכנונים יומיים קיימים
  const getDailyPlanForDate = (dateStr) => {
    const dailyPlans = JSON.parse(localStorage.getItem('dailyPlans') || '[]');
    const userPlans = dailyPlans.filter(p => 
      p.userId === currentUser?.id || p.userName === currentUser?.name
    );
    return userPlans.find(p => p.date === dateStr);
  };

  // תיקון: פונקציה משופרת לבדיקה האם שעה חסומה על ידי תכנון יומי
  const isTimeBlockedByDailyPlan = (dateStr, timeSlot) => {
    const dailyPlan = getDailyPlanForDate(dateStr);
    if (!dailyPlan?.schedule) return false;
    
    // בדיקה ישירה אם יש משימה בשעה הזו
    if (dailyPlan.schedule[timeSlot]) {
      return true;
    }
    
    // בדיקה אם השעה הזו חסומה על ידי משימה קודמת עם duration
    const times = Object.keys(dailyPlan.schedule).sort();
    for (const time of times) {
      if (time < timeSlot && dailyPlan.schedule[time]) {
        const slot = dailyPlan.schedule[time];
        if (slot?.duration) {
          const duration = slot.duration;
          const startHour = parseInt(time.split(':')[0]);
          const startMinute = parseInt(time.split(':')[1]);
          const endTime = new Date(2024, 0, 1, startHour, startMinute);
          endTime.setMinutes(endTime.getMinutes() + duration * 60);
          
          const currentHour = parseInt(timeSlot.split(':')[0]);
          const currentMinute = parseInt(timeSlot.split(':')[1]);
          const currentTime = new Date(2024, 0, 1, currentHour, currentMinute);
          
          if (currentTime < endTime) {
            return true; // השעה הזו חסומה
          }
        }
      }
    }
    
    return false;
  };

  // תיקון: פונקציה לבדיקה האם שעה חסומה על ידי תכנון שבועי קיים
  const isTimeBlockedByExistingWeeklyPlan = (dateStr, timeSlot) => {
    if (!isAddingToExisting || !existingSchedule) return false;
    
    // בדיקה אם יש משימה קיימת בשעה זו
    if (existingSchedule[dateStr]?.[timeSlot]) {
      return true;
    }
    
    // בדיקת המשך משימות
    if (!existingSchedule[dateStr]) return false;
    
    const times = Object.keys(existingSchedule[dateStr]).sort();
    for (const time of times) {
      if (time < timeSlot && existingSchedule[dateStr][time]) {
        const task = existingSchedule[dateStr][time];
        const duration = task.duration || 1;
        
        const startHour = parseInt(time.split(':')[0]);
        const startMinute = parseInt(time.split(':')[1]);
        const endTime = new Date(2024, 0, 1, startHour, startMinute);
        endTime.setMinutes(endTime.getMinutes() + duration * 60);
        
        const currentHour = parseInt(timeSlot.split(':')[0]);
        const currentMinute = parseInt(timeSlot.split(':')[1]);
        const currentTime = new Date(2024, 0, 1, currentHour, currentMinute);
        
        if (currentTime < endTime) {
          return true;
        }
      }
    }
    
    return false;
  };

  // תיקון: פונקציה לקבלת משימה מתכנון קיים
  const getExistingTaskForSlot = (dateStr, timeSlot) => {
    if (!isAddingToExisting || !existingSchedule) return null;
    
    // משימה ישירה
    if (existingSchedule[dateStr]?.[timeSlot]) {
      return {
        ...existingSchedule[dateStr][timeSlot],
        isExisting: true
      };
    }
    
    // המשך משימה
    if (!existingSchedule[dateStr]) return null;
    
    const times = Object.keys(existingSchedule[dateStr]).sort();
    for (const time of times) {
      if (time < timeSlot && existingSchedule[dateStr][time]) {
        const task = existingSchedule[dateStr][time];
        const duration = task.duration || 1;
        
        const startHour = parseInt(time.split(':')[0]);
        const startMinute = parseInt(time.split(':')[1]);
        const endTime = new Date(2024, 0, 1, startHour, startMinute);
        endTime.setMinutes(endTime.getMinutes() + duration * 60);
        
        const currentHour = parseInt(timeSlot.split(':')[0]);
        const currentMinute = parseInt(timeSlot.split(':')[1]);
        const currentTime = new Date(2024, 0, 1, currentHour, currentMinute);
        
        if (currentTime < endTime) {
          return {
            ...task,
            isExisting: true,
            isContinuation: true
          };
        }
      }
    }
    
    return null;
  };

  // פונקציה לקבלת ישיבה עבור תאריך וזמן מסוימים
  const getMeetingForSlot = (dateStr, timeSlot) => {
    const dayMeetings = weeklyScheduledMeetings[dateStr] || [];
    if (dayMeetings.length === 0) return null;
    
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const slotTime = new Date(2024, 0, 1, hours, minutes);
    
    for (const meeting of dayMeetings) {
      const startDate = meeting.startDate?.toDate ? meeting.startDate.toDate() : new Date(meeting.startDate);
      const endDate = meeting.endDate?.toDate ? meeting.endDate.toDate() : new Date(meeting.endDate);
      
      const startTime = new Date(2024, 0, 1, startDate.getHours(), startDate.getMinutes());
      const endTime = new Date(2024, 0, 1, endDate.getHours(), endDate.getMinutes());
      
      if (slotTime >= startTime && slotTime < endTime) {
        return {
          ...meeting,
          isStart: slotTime.getTime() === startTime.getTime(),
          isContinuation: slotTime.getTime() !== startTime.getTime()
        };
      }
    }
    
    return null;
  };

  // פונקציה לבדיקה האם זמן חסום על ידי ישיבה
  const isTimeBlockedByMeeting = (dateStr, timeSlot) => {
    return getMeetingForSlot(dateStr, timeSlot) !== null;
  };

  // בדיקה אם משימה כבר תוכננה
  const checkIfTaskAlreadyScheduled = (taskId) => {
    try {
      // בדיקת תכנונים יומיים
      const dailyPlans = JSON.parse(localStorage.getItem('dailyPlans') || '[]');
      for (const plan of dailyPlans) {
        // דלג על התאריכים של השבוע הנוכחי
        const planDate = new Date(plan.date);
        const isInCurrentWeek = defaultWeekDates.some(weekDate => 
          weekDate.toISOString().split('T')[0] === plan.date
        );
        if (isInCurrentWeek) continue;
        
        if (plan.schedule) {
          for (const slot of Object.values(plan.schedule)) {
            if (slot?.task?.id === taskId) {
              return { type: 'daily', date: plan.date };
            }
          }
        }
      }

      // בדיקת תכנונים שבועיים אחרים
      const weeklyPlans = JSON.parse(localStorage.getItem('weeklyPlans') || '[]');
      const currentWeekStart = defaultWeekDates[0]?.toISOString().split('T')[0];
      
      for (const plan of weeklyPlans) {
        // דלג על השבוע הנוכחי
        if (plan.summaryData?.weekStart === currentWeekStart) continue;
        
        if (plan.summaryData?.schedule) {
          for (const [dateStr, daySchedule] of Object.entries(plan.summaryData.schedule)) {
            for (const task of Object.values(daySchedule || {})) {
              if (task?.id === taskId) {
                return { type: 'weekly', date: dateStr };
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error checking scheduled tasks:', error);
    }
    
    return null;
  };

  // אתחול משכי זמן
  useEffect(() => {
    const durations = {};
    selectedTasks.forEach(task => {
      durations[task.id] = {};
      defaultWeekDates.forEach(date => {
        const dateStr = date.toISOString().split('T')[0];
        durations[task.id][dateStr] = 1;
      });
    });
    setTaskDurations(durations);
  }, [selectedTasks, defaultWeekDates]);

  // טעינת ישיבות מתוכננות לכל השבוע
  useEffect(() => {
    if (!currentUser || meetings.length === 0) return;
    
    const weeklyMeetings = {};
    
    defaultWeekDates.forEach(date => {
      const dateStr = date.toISOString().split('T')[0];
      
      // מצא ישיבות שמתוכננות לתאריך זה
      const dayMeetings = meetings.filter(meeting => {
        // רק ישיבות מתוכננות
        if (meeting.status !== 'scheduled') return false;
        
        // בדוק אם המשתמש משתתף בישיבה
        if (!meeting.participantIds || !meeting.participantIds.includes(currentUser.id)) {
          return false;
        }
        
        // בדוק אם המשתמש אישר הגעה
        if (!meeting.confirmedParticipantIds || !meeting.confirmedParticipantIds.includes(currentUser.id)) {
          return false;
        }
        
        // בדוק אם הישיבה היא באותו יום
        const meetingDate = meeting.startDate?.toDate ? meeting.startDate.toDate() : new Date(meeting.startDate);
        const meetingDateStr = meetingDate.toISOString().split('T')[0];
        
        return meetingDateStr === dateStr;
      });
      
      weeklyMeetings[dateStr] = dayMeetings;
    });
    
    setWeeklyScheduledMeetings(weeklyMeetings);
  }, [defaultWeekDates, meetings, currentUser]);

  // תיקון: אתחול לוח שבועי עם טעינת תכנון קיים
  useEffect(() => {
    const initialSchedule = { ...weeklySchedule };
    
    // אם יש תכנון קיים, טען אותו
    if (isAddingToExisting && existingSchedule) {
      Object.keys(existingSchedule).forEach(dateStr => {
        if (!initialSchedule[dateStr]) {
          initialSchedule[dateStr] = {};
        }
        // העתק רק את המשימות הקיימות
        Object.keys(existingSchedule[dateStr]).forEach(time => {
          initialSchedule[dateStr][time] = existingSchedule[dateStr][time];
        });
      });
    }
    
    // השלם את השעות החסרות
    defaultWeekDates.forEach(date => {
      const dateStr = date.toISOString().split('T')[0];
      if (!initialSchedule[dateStr]) {
        initialSchedule[dateStr] = {};
      }
      timeSlots.forEach(slot => {
        if (initialSchedule[dateStr][slot.time] === undefined) {
          initialSchedule[dateStr][slot.time] = null;
        }
      });
    });
    
    setWeeklySchedule(initialSchedule);
  }, [defaultWeekDates, isAddingToExisting, existingSchedule]);

  // תיקון: עדכון חישוב שעות חסומות
// תיקון: עדכון חישוב שעות חסומות
useEffect(() => {
  const dailyBlocked = {};
  const weeklyBlocked = {};
  
  defaultWeekDates.forEach(date => {
    const dateStr = date.toISOString().split('T')[0];
    dailyBlocked[dateStr] = new Set();
    weeklyBlocked[dateStr] = new Set();
    
    // בדיקת תכנונים יומיים
    const dailyPlan = getDailyPlanForDate(dateStr);
    if (dailyPlan?.schedule) {
      Object.keys(dailyPlan.schedule).forEach(time => {
        dailyBlocked[dateStr].add(time);
        
        // הוסף גם שעות המשך
        const slot = dailyPlan.schedule[time];
        if (slot?.duration && slot.duration > 0.5) {
          const timeIndex = timeSlots.findIndex(ts => ts.time === time);
          const slotsToBlock = Math.ceil(slot.duration * 2) - 1;
          for (let i = 1; i <= slotsToBlock && timeIndex + i < timeSlots.length; i++) {
            dailyBlocked[dateStr].add(timeSlots[timeIndex + i].time);
          }
        }
      });
    }
    
    // בדיקת תכנון שבועי קיים (כולל משימות שכבר נגררו!)
    if (weeklySchedule[dateStr]) {
      Object.keys(weeklySchedule[dateStr]).forEach(time => {
        const task = weeklySchedule[dateStr][time];
        if (task && task.title) {
          weeklyBlocked[dateStr].add(time);
          
          // הוסף גם שעות המשך
          const duration = taskDurations[task.id]?.[dateStr] || task.duration || 1;
          if (duration > 0.5) {
            const timeIndex = timeSlots.findIndex(ts => ts.time === time);
            const slotsToBlock = Math.ceil(duration * 2) - 1;
            for (let i = 1; i <= slotsToBlock && timeIndex + i < timeSlots.length; i++) {
              weeklyBlocked[dateStr].add(timeSlots[timeIndex + i].time);
            }
          }
        }
      });
    }
    
    // בדיקת תכנון שבועי קיים מ-existingSchedule (אם במצב הוספה)
    if (isAddingToExisting && existingSchedule?.[dateStr]) {
      Object.keys(existingSchedule[dateStr]).forEach(time => {
        if (existingSchedule[dateStr][time]) {
          weeklyBlocked[dateStr].add(time);
          
          // הוסף גם שעות המשך
          const task = existingSchedule[dateStr][time];
          const duration = task.duration || 1;
          if (duration > 0.5) {
            const timeIndex = timeSlots.findIndex(ts => ts.time === time);
            const slotsToBlock = Math.ceil(duration * 2) - 1;
            for (let i = 1; i <= slotsToBlock && timeIndex + i < timeSlots.length; i++) {
              weeklyBlocked[dateStr].add(timeSlots[timeIndex + i].time);
            }
          }
        }
      });
    }
  });
  
  setDailyPlanBlockedSlots(dailyBlocked);
  setWeeklyPlanBlockedSlots(weeklyBlocked);
  setBlockedSlots(weeklyBlocked); // הוסף גם את זה!
}, [defaultWeekDates, isAddingToExisting, existingSchedule, weeklySchedule, taskDurations]);

  // עדכון משימות זמינות
  useEffect(() => {
    if (propAvailableTasks) {
      setAvailableTasks(propAvailableTasks);
    } else {
      const allScheduledTaskIds = new Set();
      const alreadyScheduled = [];
      
      // משימות שכבר בלוח השבועי הנוכחי
      Object.values(weeklySchedule).forEach(daySchedule => {
        Object.values(daySchedule).forEach(task => {
          if (task && task.id) {
            allScheduledTaskIds.add(task.id);
          }
        });
      });
      
      // משימות שתוכננו מחוץ לשבוע הנוכחי
      selectedTasks.forEach(task => {
        const scheduledInfo = checkIfTaskAlreadyScheduled(task.id);
        if (scheduledInfo) {
          alreadyScheduled.push({
            ...task,
            scheduledDate: scheduledInfo.date,
            scheduledType: scheduledInfo.type
          });
          allScheduledTaskIds.add(task.id);
        }
      });
      
      setAlreadyScheduledTasks(alreadyScheduled);
      
      const unscheduledTasks = selectedTasks.filter(task => !allScheduledTaskIds.has(task.id));
      setAvailableTasks(unscheduledTasks);
    }
  }, [weeklySchedule, selectedTasks, propAvailableTasks]);

  // חישוב חסימות
  useEffect(() => {
    const blocked = {};
    defaultWeekDates.forEach(date => {
      const dateStr = date.toISOString().split('T')[0];
      blocked[dateStr] = new Set();
      
      const daySchedule = weeklySchedule[dateStr] || {};
      Object.entries(daySchedule).forEach(([time, task]) => {
        if (task && !task.isExisting) { // אל תחשב משימות קיימות
          const duration = taskDurations[task.id]?.[dateStr] || 1;
          if (duration > 0.5) {
            const timeIndex = timeSlots.findIndex(ts => ts.time === time);
            const slotsToBlock = Math.ceil(duration * 2) - 1;
            for (let i = 1; i <= slotsToBlock && timeIndex + i < timeSlots.length; i++) {
              blocked[dateStr].add(timeSlots[timeIndex + i].time);
            }
          }
        }
      });
    });
    setBlockedSlots(blocked);
  }, [weeklySchedule, taskDurations, defaultWeekDates]);

  // פונקציות גרירה
  const handleDragStart = (e, task, from) => {
    // אל תאפשר גרירת משימות קיימות
    if (task.isExisting) {
      e.preventDefault();
      return;
    }
    
    setDraggedTask(task);
    setDraggedFrom(from);
    setIsDragging(true);
    dragRef.current = task;
    
    const ghost = document.createElement('div');
    ghost.className = 'bg-white shadow-xl p-3 rounded-lg opacity-90 pointer-events-none';
    ghost.innerHTML = `<div class="font-medium">${task.title}</div>`;
    ghost.style.position = 'absolute';
    ghost.style.width = '250px';
    ghost.style.zIndex = '9999';
    ghost.style.left = '-9999px';
    document.body.appendChild(ghost);
    ghostRef.current = ghost;
    e.dataTransfer.setDragImage(ghost, 125, 20);
    
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDraggedFrom(null);
    setDropTarget(null);
    setIsDragging(false);
    dragRef.current = null;
    
    if (ghostRef.current) {
      document.body.removeChild(ghostRef.current);
      ghostRef.current = null;
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, targetId) => {
    e.preventDefault();
    setDropTarget(targetId);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    
    if (!draggedTask || !draggedFrom) return;

    const newSchedule = { ...weeklySchedule };

    // מקור: רשימת משימות
    if (draggedFrom === 'available') {
      if (targetId.startsWith('time-')) {
        const time = targetId.replace('time-', '');
        
        // תיקון: בדיקות התנגשות משופרות
        if (dailyPlanBlockedSlots[currentDateStr]?.has(time)) {
          alert('❌ לא ניתן להוסיף משימה - כבר קיימת משימה מתכנון יומי באותה שעה');
          
          handleDragEnd();

          return;
        }
        if (blockedSlots[currentDateStr]?.has(time)) {
  alert('❌ לא ניתן להוסיף משימה - השעה תפוסה על ידי משימה אחרת');
  handleDragEnd();
  return;
}
        if (weeklyPlanBlockedSlots[currentDateStr]?.has(time)) {
          alert('❌ לא ניתן להוסיף משימה - כבר קיימת משימה בתכנון השבועי באותה שעה');
          handleDragEnd();
          return;
        }
        
        if (!blockedSlots[currentDateStr]?.has(time) && !newSchedule[currentDateStr][time]) {
          if (!newSchedule[currentDateStr]) {
            newSchedule[currentDateStr] = {};
            timeSlots.forEach(slot => {
              newSchedule[currentDateStr][slot.time] = null;
            });
          }
          newSchedule[currentDateStr][time] = draggedTask;
          
          if (onTaskDragged) {
            onTaskDragged(draggedTask.id, `day_${currentDayIndex}`, time);
          }
        }
      }
    }
    // מקור: שעה בלוח
    else if (draggedFrom.startsWith('time-')) {
      const sourceTime = draggedFrom.replace('time-', '');
      
      // יעד: רשימת משימות
      if (targetId === 'available') {
        newSchedule[currentDateStr][sourceTime] = null;
        if (onTaskRemoved) {
          onTaskRemoved(draggedTask.id, `day_${currentDayIndex}`);
        }
      }
      // יעד: שעה אחרת
      else if (targetId.startsWith('time-')) {
        const destTime = targetId.replace('time-', '');
        
        // תיקון: בדיקות התנגשות משופרות
        if (dailyPlanBlockedSlots[currentDateStr]?.has(destTime)) {
          alert('❌ לא ניתן להעביר משימה - כבר קיימת משימה מתכנון יומי באותה שעה');
          handleDragEnd();
          return;
        }
        
        if (weeklyPlanBlockedSlots[currentDateStr]?.has(destTime)) {
          alert('❌ לא ניתן להעביר משימה - כבר קיימת משימה בתכנון השבועי באותה שעה');
          handleDragEnd();
          return;
        }
        
        if (!blockedSlots[currentDateStr]?.has(destTime) && !newSchedule[currentDateStr][destTime]) {
          newSchedule[currentDateStr][sourceTime] = null;
          newSchedule[currentDateStr][destTime] = draggedTask;
        }
      }
    }

    setWeeklySchedule(newSchedule);
    handleDragEnd();
  };

  const updateTaskDuration = (taskId, dateStr, time, change) => {
    const currentDuration = taskDurations[taskId]?.[dateStr] || 1;
    const newDuration = Math.max(0.5, Math.min(4, currentDuration + change));
    
    const timeIndex = timeSlots.findIndex(ts => ts.time === time);
    const slotsNeeded = Math.ceil(newDuration * 2);
    
    let canExpand = true;
    for (let i = 1; i < slotsNeeded && timeIndex + i < timeSlots.length; i++) {
      const nextTime = timeSlots[timeIndex + i]?.time;
      if (nextTime) {
        // בדוק תכנונים יומיים
        if (dailyPlanBlockedSlots[dateStr]?.has(nextTime)) {
          canExpand = false;
          break;
        }
        // בדוק תכנון שבועי קיים
        if (weeklyPlanBlockedSlots[dateStr]?.has(nextTime)) {
          canExpand = false;
          break;
        }
        // בדוק משימות אחרות
        if (weeklySchedule[dateStr]?.[nextTime]) {
          canExpand = false;
          break;
        }
      }
    }

    if (canExpand || newDuration < currentDuration) {
      setTaskDurations(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          [dateStr]: newDuration
        }
      }));
    } else {
      alert('❌ לא ניתן להרחיב את המשימה - יש משימות חוסמות');
    }
  };

  const removeTask = (time) => {
    const newSchedule = { ...weeklySchedule };
    const task = newSchedule[currentDateStr]?.[time];
    
    if (task && newSchedule[currentDateStr] && !task.isExisting) {
      newSchedule[currentDateStr][time] = null;
      setWeeklySchedule(newSchedule);
      
      if (onTaskRemoved) {
        onTaskRemoved(task.id, `day_${currentDayIndex}`);
      }
    }
  };

  const formatDuration = (duration) => {
    if (duration === 0.5) return 'חצי שעה';
    if (duration === 1) return 'שעה';
    if (duration === 1.5) return 'שעה וחצי';
    if (duration % 1 === 0) return `${duration} שעות`;
    return `${Math.floor(duration)} וחצי שעות`;
  };

  const calculateStats = () => {
    let totalScheduled = 0;
    let totalHours = 0;
    let todayScheduled = 0;
    let todayHours = 0;
    let existingTasks = 0;

    Object.entries(weeklySchedule).forEach(([dateStr, daySchedule]) => {
      Object.values(daySchedule).forEach(task => {
        if (task) {
          if (task.isExisting) {
            existingTasks++;
          } else {
            totalScheduled++;
            const duration = taskDurations[task.id]?.[dateStr] || 1;
            totalHours += duration;
            
            if (dateStr === currentDateStr) {
              todayScheduled++;
              todayHours += duration;
            }
          }
        }
      });
    });

    return {
      totalScheduled,
      totalHours,
      todayScheduled,
      todayHours,
      totalTasks: selectedTasks.length,
      alreadyScheduled: alreadyScheduledTasks.length,
      existingTasks
    };
  };

  const getPermissionMessage = () => {
    const role = currentUser?.role;
    const teamName = currentUser?.teamName;
    const deptName = currentUser?.departmentName;

    switch (role) {
      case 'team_manager':
        return `מוצגות משימות של צוות: ${teamName || 'לא מוגדר'}`;
      case 'dept_manager':
        return `מוצגות משימות של מחלקת: ${deptName || 'לא מוגדר'}`;
      case 'employee':
        return 'מוצגות המשימות שלך בלבד';
      case 'org_admin':
      case 'ceo':
      case 'deputy_ceo':
        return 'מוצגות כל המשימות בארגון';
      default:
        return 'מוצגות משימות לפי הרשאות';
    }
  };

  const stats = calculateStats();

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {isAddingToExisting 
            ? 'הוספת משימות לתכנון שבועי קיים' 
            : 'תכנון שבועי - ארגון המשימות'}
        </h2>
        
        {/* תיקון: הודעה על מצב הוספה */}
        {isAddingToExisting && (
          <div className="mb-4 p-3 bg-yellow-100 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-yellow-800 font-medium">
              שים לב: אתה מוסיף משימות לתכנון קיים. שעות תפוסות מסומנות באפור וננעלות.
            </span>
          </div>
        )}
        
        {/* הודעת הרשאות */}
        <div className="mb-4 p-3 bg-gray-100 rounded-lg flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-600" />
          <span className="text-sm text-gray-800">
            {getPermissionMessage()}
          </span>
        </div>
        
        {/* התראה על משימות שתוכננו */}
        {stats.alreadyScheduled > 0 && (
          <div className="mb-4 p-3 bg-orange-100 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">
                {stats.alreadyScheduled} משימות כבר תוכננו מחוץ לשבוע זה
              </span>
            </div>
            <div className="text-xs text-orange-700">
              {alreadyScheduledTasks.slice(0, 3).map((task, index) => (
                <div key={index}>
                  • {task.title} - {new Date(task.scheduledDate).toLocaleDateString('he-IL')}
                </div>
              ))}
              {alreadyScheduledTasks.length > 3 && (
                <div>ועוד {alreadyScheduledTasks.length - 3} משימות...</div>
              )}
            </div>
          </div>
        )}
        
        {/* ניווט בין ימים */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentDayIndex(Math.max(0, currentDayIndex - 1));
            }}
            disabled={currentDayIndex === 0}
            className="p-2 rounded-lg bg-white shadow hover:shadow-md disabled:opacity-50 transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <div className="flex gap-2">
            {dayNames.map((day, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentDayIndex(index);
                }}
                className={`px-4 py-2 rounded-lg transition-all ${
                  currentDayIndex === index
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="font-bold">{day}</div>
                <div className="text-xs">
                  {defaultWeekDates[index]?.toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric' })}
                </div>
              </button>
            ))}
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentDayIndex(Math.min(6, currentDayIndex + 1));
            }}
            disabled={currentDayIndex === 6}
            className="p-2 rounded-lg bg-white shadow hover:shadow-md disabled:opacity-50 transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        
        {/* סטטיסטיקות */}
        <div className="grid grid-cols-6 gap-3">
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.todayScheduled}</div>
            <div className="text-xs text-gray-600">משימות היום</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.todayHours}h</div>
            <div className="text-xs text-gray-600">שעות היום</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.totalScheduled}</div>
            <div className="text-xs text-gray-600">סה"כ מתוכנן</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.totalHours}h</div>
            <div className="text-xs text-gray-600">סה"כ שעות</div>
          </div>
          {isAddingToExisting && (
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-600">{stats.existingTasks}</div>
              <div className="text-xs text-gray-600">קיימות</div>
            </div>
          )}
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-600">
              {availableTasks.length}
            </div>
            <div className="text-xs text-gray-600">זמינות</div>
          </div>
        </div>
      </div>

      <div className="flex gap-6 flex-1" style={{ maxHeight: '700px' }}>
        {/* משימות זמינות */}
        <div className="w-1/3">
          <div className="bg-white rounded-xl shadow-lg p-6 h-full overflow-hidden flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-green-500" />
              משימות זמינות ({availableTasks.length})
            </h3>
            
            <div
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, 'available')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'available')}
              className={`flex-1 overflow-y-auto space-y-2 p-2 rounded-lg transition-colors ${
                dropTarget === 'available' ? 'bg-gray-100 ring-2 ring-blue-400' : ''
              }`}
              style={{ maxHeight: '600px' }}
            >
              {availableTasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CalendarDays className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p>כל המשימות תוכננו!</p>
                  {stats.alreadyScheduled > 0 && (
                    <p className="text-xs mt-2">
                      ({stats.alreadyScheduled} משימות תוכננו מחוץ לשבוע זה)
                    </p>
                  )}
                </div>
              ) : (
                availableTasks.map((task) => (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task, 'available')}
                    onDragEnd={handleDragEnd}
                    className={`p-3 bg-gradient-to-r cursor-move ${
                      task.priority === 'high' 
                        ? 'from-red-50 to-pink-50 border-red-200' 
                        : task.priority === 'medium'
                        ? 'from-yellow-50 to-orange-50 border-yellow-200'
                        : 'from-green-50 to-teal-50 border-green-200'
                    } border rounded-lg transition-all hover:shadow-md ${
                      draggedTask?.id === task.id ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{task.title}</div>
                        {task.description && (
                          <div className="text-sm text-gray-600 mt-1">{task.description}</div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            task.priority === 'high'
                              ? 'bg-red-100 text-red-700'
                              : task.priority === 'medium'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {task.priority === 'high' ? 'דחוף' : 
                             task.priority === 'medium' ? 'בינוני' : 'רגיל'}
                          </span>
                          {task.projectName && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                              {task.projectName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* לוח זמנים ליום נבחר */}
        <div className="flex-1">
          <div className="bg-white rounded-xl shadow-lg p-6 h-full overflow-hidden">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              יום {currentDayName} - {currentDate?.toLocaleDateString('he-IL')}
            </h3>
            
            <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
              <div className="space-y-2">
                {timeSlots.map((slot) => {
                  const hasDailyTask = isTimeBlockedByDailyPlan(currentDateStr, slot.time);
                  const hasExistingWeeklyTask = isTimeBlockedByExistingWeeklyPlan(currentDateStr, slot.time);
                  const existingTask = getExistingTaskForSlot(currentDateStr, slot.time);
                  const meetingForSlot = getMeetingForSlot(currentDateStr, slot.time);
                  const isBlocked = blockedSlots[currentDateStr]?.has(slot.time);
                  const task = weeklySchedule[currentDateStr]?.[slot.time];
                  const canDrop = !isBlocked && !task && !hasDailyTask && !hasExistingWeeklyTask && !meetingForSlot;
                  
                  return (
                    <div
                      key={slot.time}
                      onDragOver={canDrop ? handleDragOver : null}
                      onDragEnter={canDrop ? (e) => handleDragEnter(e, `time-${slot.time}`) : null}
                      onDragLeave={canDrop ? handleDragLeave : null}
                      onDrop={canDrop ? (e) => handleDrop(e, `time-${slot.time}`) : null}
                      className={`flex items-stretch gap-3 p-3 rounded-lg transition-all ${
                        isBlocked 
                          ? 'bg-gray-100 opacity-50' 
                          : meetingForSlot
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : hasDailyTask
                          ? 'bg-blue-50 border-l-4 border-blue-400'
                          : hasExistingWeeklyTask
                          ? 'bg-purple-50 border-l-4 border-purple-400'
                          : task
                          ? 'bg-gray-50'
                          : dropTarget === `time-${slot.time}`
                          ? 'bg-green-50 scale-[1.02] ring-2 ring-green-400' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      {/* זמן ואייקון */}
                      <div className="w-24 flex items-center gap-2">
                        {slot.icon && (
                          <slot.icon className={`w-5 h-5 bg-gradient-to-r ${slot.color} text-white rounded p-1`} />
                        )}
                        <span className="font-mono font-bold text-gray-700">{slot.time}</span>
                      </div>
                      
                      {/* משימה או placeholder */}
                      <div className="flex-1" style={{ minHeight: '50px' }}>
                        {isBlocked ? (
                          <div className="text-center text-gray-400 text-sm py-2">
                            (המשך משימה)
                          </div>
                        ) : meetingForSlot ? (
                          meetingForSlot.isContinuation ? (
                            <div className="p-3 bg-blue-100 border-2 border-blue-400 rounded-lg">
                              <div className="text-center text-blue-600 text-sm">
                                ↓ המשך ישיבה ↓
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 bg-blue-100 border-2 border-blue-400 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <CalendarDays className="w-5 h-5 text-blue-600" />
                                <div className="font-bold text-blue-900">
                                  📅 {meetingForSlot.subject}
                                </div>
                              </div>
                              <div className="text-sm text-blue-700 mb-1">
                                🎯 {meetingForSlot.purpose}
                              </div>
                              {meetingForSlot.location && (
                                <div className="text-xs text-blue-600 mb-1">
                                  📍 {meetingForSlot.location}
                                </div>
                              )}
                              <div className="text-xs text-blue-600 flex items-center gap-2">
                                <Clock className="w-3 h-3" />
                                {new Date(meetingForSlot.startDate?.toDate ? meetingForSlot.startDate.toDate() : meetingForSlot.startDate).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })} - 
                                {new Date(meetingForSlot.endDate?.toDate ? meetingForSlot.endDate.toDate() : meetingForSlot.endDate).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          )
                        ) : hasDailyTask ? (
                          <div className="p-3 bg-blue-100 border-2 border-blue-300 rounded-lg opacity-75">
                            <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4 text-blue-600" />
                              <div className="font-medium text-gray-700">
                                {(() => {
                                  const dailyPlan = getDailyPlanForDate(currentDateStr);
                                  if (dailyPlan?.schedule?.[slot.time]) {
                                    return dailyPlan.schedule[slot.time].task?.title || 'משימה מתכנון יומי';
                                  }
                                  return '(המשך משימה מתכנון יומי)';
                                })()}
                              </div>
                            </div>
                            <div className="text-xs text-blue-600 mt-1">
                              📅 מתכנון יומי - לא ניתן לערוך
                            </div>
                          </div>
                        ) : existingTask ? (
                          existingTask.isContinuation ? (
                            <div className="p-3 bg-purple-100 border-2 border-purple-300 rounded-lg opacity-75">
                              <div className="text-center text-purple-600 text-sm">
                                ↓ המשך משימה קיימת ↓
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 bg-purple-100 border-2 border-purple-300 rounded-lg opacity-75">
                              <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-purple-600" />
                                <div className="font-medium text-gray-700">
                                  {existingTask.title}
                                </div>
                              </div>
                              <div className="text-xs text-purple-600 mt-1">
                                🔒 משימה קיימת - לא ניתן לערוך
                              </div>
                              {existingTask.duration && (
                                <div className="text-xs text-purple-600 mt-1">
                                  משך: {formatDuration(existingTask.duration)}
                                </div>
                              )}
                            </div>
                          )
                        ) : task ? (
                          <div
                            draggable={!task.isExisting}
                            onDragStart={task.isExisting ? null : (e) => handleDragStart(e, task, `time-${slot.time}`)}
                            onDragEnd={task.isExisting ? null : handleDragEnd}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (task.id) {
                                onTaskClick(task.id);
                              }
                            }}
                            className={`p-3 bg-white border-2 rounded-lg cursor-pointer ${
                              task.isExisting ? 'opacity-75' : ''
                            } ${
                              task.priority === 'high'
                                ? 'border-red-300 bg-red-50'
                                : task.priority === 'medium'
                                ? 'border-yellow-300 bg-yellow-50'
                                : 'border-green-300 bg-green-50'
                            } ${draggedTask?.id === task.id ? 'opacity-50' : ''}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex items-start gap-2 flex-1">
                                {task.isExisting ? (
                                  <Lock className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                                ) : (
                                  <GripVertical className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                                )}
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    {task.title}
                                  </div>
                                  {task.projectName && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {task.projectName}
                                    </div>
                                  )}
                                  {task.isExisting && (
                                    <div className="text-xs text-purple-600 mt-1">
                                      🔒 משימה קיימת
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* משך זמן ומחיקה - רק למשימות לא קיימות */}
                              {!task.isExisting && (
                                <div className="flex items-center gap-2 mr-2">
                                  <div className="bg-white border border-gray-200 rounded-lg px-2 py-1">
                                    <div className="text-xs text-gray-500 mb-1">משך זמן:</div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateTaskDuration(
                                            task.id,
                                            currentDateStr,
                                            slot.time,
                                            -0.5
                                          );
                                        }}
                                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                                      >
                                        <Minus className="w-3 h-3" />
                                      </button>
                                      <span className="text-sm font-bold px-2 min-w-[80px] text-center">
                                        {formatDuration(taskDurations[task.id]?.[currentDateStr] || 1)}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateTaskDuration(
                                            task.id,
                                            currentDateStr,
                                            slot.time,
                                            0.5
                                          );
                                        }}
                                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                                      >
                                        <Plus className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeTask(slot.time);
                                    }}
                                    className="p-2 hover:bg-red-100 rounded text-red-500 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-12 border-2 border-dashed border-gray-300 rounded-lg text-gray-400">
                            גרור משימה לכאן
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* כפתורי ניווט */}
      <div className="mt-6 flex justify-between">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all flex items-center gap-2"
        >
          <ChevronLeft className="w-5 h-5" />
          חזור
        </button>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          disabled={stats.totalScheduled === 0 && !isAddingToExisting}
          className={`px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center gap-3 ${
            stats.totalScheduled > 0 || isAddingToExisting
              ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white hover:from-green-600 hover:to-blue-600 shadow-lg hover:shadow-xl transform hover:scale-105'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          המשך לסיכום
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default WeeklyTimeSchedule;